import { useSignUp } from '@clerk/react';
import {
  Alert,
  AlertDescription,
  Card,
  CardContent,
  Spinner,
} from '@design-system';
import { CheckCircle2, Mail } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { WaterlilyLogo } from '@client/components/WaterlilyLogo';

type Display =
  | { kind: 'pending' }
  | { kind: 'success'; title: string; body: string }
  | { kind: 'error'; title: string; body: string };

function describe(status: string | null | undefined): Display {
  switch (status) {
    case 'verified':
    case 'verified_from_the_same_client':
      return {
        kind: 'success',
        title: 'Signed up on other tab',
        body: 'Return to original tab to continue',
      };
    case 'expired':
      return {
        kind: 'error',
        title: 'This verification link has expired',
        body: 'Return to the original tab to continue.',
      };
    case 'failed':
      return {
        kind: 'error',
        title: 'This verification link is invalid',
        body: 'Return to the original tab to continue.',
      };
    case 'client_mismatch':
      return {
        kind: 'error',
        title: 'Verification link is invalid for this device',
        body: 'To continue, open the verification link on the device and browser from which you initiated the sign-up',
      };
    default:
      return { kind: 'pending' };
  }
}

export function EmailLinkSignupVerification() {
  const { signUp } = useSignUp();
  const [searchParams] = useSearchParams();
  const status = signUp?.verifications.emailLinkVerification?.status ?? searchParams.get('__clerk_status') ?? null;
  const display = describe(status);

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <header className="fixed left-0 right-0 top-0 z-50 flex items-center border-b bg-white px-4 py-4 md:px-6">
        <WaterlilyLogo className="h-6" />
      </header>

      <main className="flex flex-1 flex-col items-center justify-center px-4 pb-12 pt-18 md:pt-20">
        <div className="w-full max-w-md">
          <Card className="overflow-hidden border bg-background p-0 gap-0 rounded-2xl shadow-none">
            <div className="p-1">
              <div className="rounded-xl bg-muted px-4 py-8 text-center">
                <div className="mb-2 flex items-center justify-center gap-2">
                  {display.kind === 'success' ? (
                    <CheckCircle2 className="size-5 text-primary-dark" />
                  ) : (
                    <Mail className="size-5 text-primary-dark" />
                  )}
                  <span className="text-xs font-semibold uppercase tracking-widest text-primary-dark">
                    Email Link
                  </span>
                </div>
                <h1 className="text-xl font-bold text-gray-950">
                  {display.kind === 'pending' ? 'Verifying your email link…' : display.title}
                </h1>
                {display.kind !== 'pending' && (
                  <p className="mx-auto mt-1 max-w-xs text-sm text-foreground">{display.body}</p>
                )}
              </div>
            </div>
            <CardContent className="p-6">
              {display.kind === 'pending' && (
                <div className="flex items-center justify-center py-6">
                  <Spinner className="size-6 text-primary" />
                </div>
              )}

              {display.kind === 'success' && (
                <Alert className="border-primary/30 bg-primary/5">
                  <AlertDescription className="justify-items-center text-primary-dark">
                    You may close this tab.
                  </AlertDescription>
                </Alert>
              )}

              {display.kind === 'error' && (
                <div className="space-y-4">
                  <Alert variant="destructive">
                    <AlertDescription>{display.body}</AlertDescription>
                  </Alert>
                  <Link
                    to="/signup"
                    className="text-primary hover:text-primary-hover block w-full text-center text-sm font-medium"
                  >
                    Back to sign up
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
